import { type ReactNode } from 'react';
import type { APInvoice } from '@erp/rpc';
import { T, useGT, Var, Num, Plural } from 'gt-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  FileTextIcon,
  CaretRightIcon,
  WarningIcon,
  CheckCircleIcon,
  XCircleIcon,
  CubeIcon,
} from '@phosphor-icons/react';
import {
  AlertContainer as Alert,
  AlertDescription,
} from '@/components/ui/alert';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useLocalStorageValue } from '@react-hookz/web';
import { RerunWorkflowStepDropdown } from '@/components/workflows/RerunWorkflowStepDropdown';
import { useRefreshAPInvoice } from '@/components/accounts-payable/invoice-detail/useRefreshAPInvoice';
import { SystemEntity } from '@erp/shared-types';
import {
  getMatchDisplayState,
  DISPLAY_STATE_BADGE_CLASSES,
  DISPLAY_STATE_LABELS,
} from '@/components/accounts-payable/shared/match-display-state';
import { PaymentStatusBadge } from '@/components/accounts-payable/shared/payment-status-badge';
import { InvoiceFields } from '@/components/accounts-payable/invoice-detail/invoice-fields';
import { TroubleDataBadges } from '@/components/common/TroubleDataBadges';

const SUPPLIER_GL_DETAILS_OPEN_KEY = 'ap-invoice-supplier-gl-details-open';
const EMPTY_EXTRACTED_TOTAL_NOTES: string[] = [];

// Helper function to generate potential errors
const generatePotentialErrors = (
  invoice: APInvoice,
): { type: 'error' | 'warning'; message: string }[] => {
  const issues: { type: 'error' | 'warning'; message: string }[] = [];

  // Check for missing required fields (errors)
  if (!invoice.invoiceNumber) {
    issues.push({ type: 'error', message: 'Missing invoice number' });
  }

  const totalAmount = Number(invoice.totalAmount);
  if (!invoice.totalAmount || Number.isNaN(totalAmount) || totalAmount <= 0) {
    issues.push({ type: 'error', message: 'Invalid total amount' });
  }

  // Check for voided invoices
  if (invoice.isVoided) {
    issues.push({ type: 'warning', message: 'Invoice has been voided' });
  }

  return issues;
};

interface InvoiceMetadataProps {
  invoice: APInvoice;
  /**
   * Work in flight on this invoice (exceptions, tasks). Rendered directly
   * under the header so it reads together with the status badge, before the
   * editable fields.
   */
  attention?: ReactNode;
  /** Extra controls for the header row, beside the rerun dropdown. */
  headerActions?: ReactNode;
  /**
   * Amber notes under Total Amount when the line-item total is not the
   * extracted document total. Does not block approval.
   */
  extractedTotalNotes?: string[];
}

function renderGLAccountSummary(
  label: string,
  glAccount: APInvoice['glAccount'] | null | undefined,
) {
  if (!glAccount) return null;

  return (
    <div className="space-y-0.5">
      <div>
        <span className="text-muted-foreground">{label}:</span>{' '}
        {glAccount.accountCode} - {glAccount.accountName}
      </div>
      {glAccount.accountType && (
        <div>
          <span className="text-muted-foreground">
            <T>Type:</T>
          </span>{' '}
          {glAccount.accountType}
        </div>
      )}
      {glAccount.accountDescription && (
        <div>
          <span className="text-muted-foreground">
            <T>Description:</T>
          </span>{' '}
          {glAccount.accountDescription}
        </div>
      )}
    </div>
  );
}

export function InvoiceMetadata({
  invoice,
  attention,
  headerActions,
  extractedTotalNotes = EMPTY_EXTRACTED_TOTAL_NOTES,
}: InvoiceMetadataProps) {
  const gt = useGT();
  // A run that just finished has usually rematched lines or rescheduled the
  // payment; pull the invoice and its activity again so the view catches up.
  const refetchInvoice = useRefreshAPInvoice();

  const issues = generatePotentialErrors(invoice);
  const displayedTrouble = invoice.troubleData;
  const errors = issues.filter((issue) => issue.type === 'error');
  const warnings = issues.filter((issue) => issue.type === 'warning');

  const displayState = getMatchDisplayState({
    status: invoice.status,
    summary: invoice.lineItemMatchSummary,
  });

  const supplierGLAccounts = invoice.supplier?.glAccounts;
  const hasSupplierGLAccounts = Boolean(
    supplierGLAccounts?.expense ||
    supplierGLAccounts?.ap ||
    supplierGLAccounts?.po,
  );

  // Remembered across sessions so an analyst who wants the reference block
  // open does not re-open it on every invoice.
  const { value: detailsOpen, set: setDetailsOpen } =
    useLocalStorageValue<boolean>(SUPPLIER_GL_DETAILS_OPEN_KEY, {
      defaultValue: false,
      initializeWithValue: true,
    });

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileTextIcon className="h-4 w-4" />
            <T>
              Vendor Invoice: <Var>{invoice.invoiceNumber || 'N/A'}</Var>
            </T>
          </CardTitle>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {headerActions}
            <RerunWorkflowStepDropdown
              variant="pills"
              entityType={SystemEntity.AP_INVOICE}
              entityId={invoice.id}
              onRunSettled={() => refetchInvoice({ id: invoice.id })}
            />
            <div className="flex items-center gap-2">
              <TroubleDataBadges troubleData={displayedTrouble} size="md" />
              <Badge className={DISPLAY_STATE_BADGE_CLASSES[displayState]}>
                {DISPLAY_STATE_LABELS[displayState]}
              </Badge>
              <PaymentStatusBadge
                status={invoice.paymentStatus}
                dueDate={invoice.dueDate}
              />
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {invoice.paymentHold && (
          <Alert className="border-amber-500 bg-amber-50 py-3">
            <WarningIcon className="h-5 w-5 text-amber-600" />
            <AlertDescription>
              <div className="font-semibold text-amber-800">
                <T>Payment on Hold</T>
              </div>
              {invoice.paymentHoldReason && (
                <div className="text-sm text-amber-700 mt-1">
                  <T>
                    Reason: <Var>{invoice.paymentHoldReason}</Var>
                  </T>
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}

        {attention}

        <InvoiceFields
          invoice={invoice}
          extractedTotalNotes={extractedTotalNotes}
        />

        {/* Error and Warning Display */}
        {(errors.length > 0 || warnings.length > 0) && (
          <div className="space-y-2">
            {errors.length > 0 && (
              <Alert className="border-red-200 bg-red-50 py-2">
                <XCircleIcon className="h-3 w-3 text-red-600" />
                <AlertDescription>
                  <div className="font-medium text-red-800 text-xs mb-1">
                    <T>
                      <Num>{errors.length}</Num>{' '}
                      <Plural n={errors.length} one="Error" other="Errors" />{' '}
                      Found
                    </T>
                  </div>
                  <ul className="text-xs text-red-700 space-y-0.5">
                    {errors.map((error, index) => (
                      <li key={index} className="flex items-center gap-1.5">
                        <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                        {error.message}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {warnings.length > 0 && (
              <Alert className="border-amber-200 bg-amber-50 py-2">
                <WarningIcon className="h-3 w-3 text-amber-600" />
                <AlertDescription>
                  <div className="font-medium text-amber-800 text-xs mb-1">
                    <T>
                      <Num>{warnings.length}</Num>{' '}
                      <Plural
                        n={warnings.length}
                        one="Warning"
                        other="Warnings"
                      />
                    </T>
                  </div>
                  <ul className="text-xs text-amber-700 space-y-0.5">
                    {warnings.map((warning, index) => (
                      <li key={index} className="flex items-center gap-1.5">
                        <span className="w-1 h-1 bg-amber-600 rounded-full"></span>
                        {warning.message}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {errors.length === 0 && warnings.length === 0 && (
              <Alert className="border-green-200 bg-green-50 py-2">
                <CheckCircleIcon className="h-3 w-3 text-green-600" />
                <AlertDescription>
                  <div className="font-medium text-green-800 text-xs">
                    <T>No issues detected - Invoice is valid for processing</T>
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {/* Origin System Sync Status (e.g., Famous Voucher) */}
        {invoice.originSyncStatus &&
          invoice.originSystem === 'famous' &&
          invoice.originEntity === 'voucher' && (
            <div className="border-t pt-3 space-y-2">
              <div className="space-y-0.5">
                <div className="text-xs font-medium text-muted-foreground">
                  <T>Famous Voucher Status</T>
                </div>
                <div className="flex items-center gap-2">
                  {invoice.originSyncStatus === 'success' ? (
                    <>
                      <CheckCircleIcon className="h-3 w-3 text-green-600" />
                      <Badge variant="outline" className="text-xs">
                        <T>Created</T>
                      </Badge>
                    </>
                  ) : invoice.originSyncStatus === 'pending' ? (
                    <>
                      <CubeIcon className="h-3 w-3 text-blue-600" />
                      <Badge variant="outline" className="text-xs">
                        <T>Pending</T>
                      </Badge>
                    </>
                  ) : invoice.originSyncStatus === 'failed' ? (
                    <>
                      <XCircleIcon className="h-3 w-3 text-red-600" />
                      <Badge variant="outline" className="text-xs">
                        <T>Failed</T>
                      </Badge>
                    </>
                  ) : null}
                </div>
              </div>
              {invoice.originId && (
                <div className="space-y-0.5">
                  <div className="text-xs font-medium text-muted-foreground">
                    <T>Entry Number</T>
                  </div>
                  <div className="text-xs font-mono">{invoice.originId}</div>
                </div>
              )}
              {invoice.originMetadata?.syncedAt &&
              typeof invoice.originMetadata.syncedAt === 'string' ? (
                <div className="space-y-0.5">
                  <div className="text-xs font-medium text-muted-foreground">
                    <T>Synced At</T>
                  </div>
                  <div className="text-xs">
                    {new Date(invoice.originMetadata.syncedAt).toLocaleString()}
                  </div>
                </div>
              ) : null}
              {invoice.originSyncErrorMessage && (
                <div className="space-y-0.5">
                  <div className="text-xs font-medium text-red-600">
                    <T>Error Message</T>
                  </div>
                  <div className="text-xs text-red-600">
                    {invoice.originSyncErrorMessage}
                  </div>
                </div>
              )}
            </div>
          )}

        {/* Supplier + GL reference details — collapsed so the line items
            stay near the top; the fields above answer the everyday questions. */}
        {(invoice.supplier || invoice.glAccount || hasSupplierGLAccounts) && (
          <Collapsible
            className="border-t pt-3"
            open={detailsOpen ?? false}
            onOpenChange={setDetailsOpen}
          >
            <CollapsibleTrigger className="group/details flex w-full items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground">
              <CaretRightIcon
                className="size-3 transition-transform group-data-[panel-open]/details:rotate-90"
                aria-hidden="true"
              />
              <T context="invoice slide-in: toggle revealing supplier contact and GL account reference details">
                Supplier & GL details
              </T>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 pt-3">
              {invoice.supplier && (
                <div className="space-y-1.5">
                  <div className="text-xs font-medium text-muted-foreground">
                    <T>Supplier Details</T>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    <div>
                      <span className="text-muted-foreground">
                        <T>Name:</T>
                      </span>{' '}
                      {invoice.supplier.name}
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        <T>Status:</T>
                      </span>{' '}
                      {invoice.supplier.status}
                    </div>
                    {invoice.supplier.contactName && (
                      <div>
                        <span className="text-muted-foreground">
                          <T>Contact:</T>
                        </span>{' '}
                        {invoice.supplier.contactName}
                      </div>
                    )}
                    {invoice.supplier.email && (
                      <div>
                        <span className="text-muted-foreground">
                          <T>Email:</T>
                        </span>{' '}
                        {invoice.supplier.email}
                      </div>
                    )}
                    {invoice.supplier.phone && (
                      <div>
                        <span className="text-muted-foreground">
                          <T>Phone:</T>
                        </span>{' '}
                        {invoice.supplier.phone}
                      </div>
                    )}
                    {invoice.supplier.paymentTerms && (
                      <div>
                        <span className="text-muted-foreground">
                          <T>Supplier Terms:</T>
                        </span>{' '}
                        {invoice.supplier.paymentTerms}
                      </div>
                    )}
                    {invoice.supplier.defaultPaymentMethod && (
                      <div>
                        <span className="text-muted-foreground">
                          <T>Payment Type:</T>
                        </span>{' '}
                        {invoice.supplier.defaultPaymentMethod.toUpperCase()}
                      </div>
                    )}
                  </div>
                  {invoice.supplier.notes &&
                    invoice.supplier.notes.length > 0 && (
                      <div className="mt-1.5">
                        <span className="text-muted-foreground text-xs">
                          <T>Notes:</T>
                        </span>
                        <ul className="text-xs mt-0.5 space-y-0.5">
                          {invoice.supplier.notes.map((note, idx) => (
                            <li key={idx} className="flex gap-1.5">
                              <span className="text-muted-foreground">-</span>
                              {note}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  {hasSupplierGLAccounts && (
                    <div className="mt-1.5 space-y-1.5">
                      <div className="text-xs font-medium text-muted-foreground">
                        <T>Supplier GL Accounts</T>
                      </div>
                      <div className="grid grid-cols-1 gap-y-1 text-xs">
                        {renderGLAccountSummary(
                          gt('Expense'),
                          supplierGLAccounts?.expense,
                        )}
                        {renderGLAccountSummary(
                          gt('AP'),
                          supplierGLAccounts?.ap,
                        )}
                        {renderGLAccountSummary(
                          gt('PO'),
                          supplierGLAccounts?.po,
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {invoice.glAccount && (
                <div className="space-y-1.5">
                  <div className="text-xs font-medium text-muted-foreground">
                    <T>GL Account</T>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    <div>
                      <span className="text-muted-foreground">
                        <T>Code:</T>
                      </span>{' '}
                      {invoice.glAccount.accountCode}
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        <T>Name:</T>
                      </span>{' '}
                      {invoice.glAccount.accountName}
                    </div>
                    {invoice.glAccount.accountType && (
                      <div>
                        <span className="text-muted-foreground">
                          <T>Type:</T>
                        </span>{' '}
                        {invoice.glAccount.accountType}
                      </div>
                    )}
                    <div>
                      <span className="text-muted-foreground">
                        <T>Control:</T>
                      </span>{' '}
                      {invoice.glAccount.isControlAccount
                        ? gt('Yes')
                        : gt('No')}
                    </div>
                    {invoice.glAccount.accountDescription && (
                      <div className="col-span-2">
                        <span className="text-muted-foreground">
                          <T>Description:</T>
                        </span>{' '}
                        {invoice.glAccount.accountDescription}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
}
